'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ComplianceJurisdiction } from '@selenos/leads';
import { MessageCircleIcon, SendIcon } from 'lucide-react';

import { useExperiment } from '~/lib/analytics/experiment-context';
import {
  trackChatWidgetEngaged,
  trackChatbotChipClicked,
  trackChatbotLeadCaptured,
  trackChatbotLeadOverlayShown,
} from '~/lib/analytics/posthog-events';

import { ChatbotLeadOverlay } from './chatbot-lead-overlay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ChatbotSectionProps {
  cityName: string;
  hourlyRate: number;
  whatsappUrl?: string;
  verticalSlug: string;
  citySlug: string;
  jurisdiction: ComplianceJurisdiction;
  verticalName: string;
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUICK_REPLIES = [
  'How much can I earn?',
  'What are the requirements?',
  'How do I start driving?',
  'What vehicle do I need?',
];

const API_URL =
  process.env.NEXT_PUBLIC_CHATSUITE_API_URL ?? 'https://api.chatsuite.akson.ch';
const MAX_RESPONSE_LENGTH = 50_000;
const LEAD_GATE_MESSAGE_COUNT = 2;

// Per-vertical widget tokens (must be literal references for Next.js build-time inlining)
const VERTICAL_WIDGET_TOKENS: Record<string, string | undefined> = {
  drivers: process.env.NEXT_PUBLIC_DRIVERS_CHATSUITE_WIDGET_TOKEN,
  'spitex-demand': process.env.NEXT_PUBLIC_SPITEX_DEMAND_CHATSUITE_WIDGET_TOKEN,
};
const DEFAULT_WIDGET_TOKEN = process.env.NEXT_PUBLIC_CHATSUITE_WIDGET_TOKEN;

// ---------------------------------------------------------------------------
// Safe markdown renderer: returns React nodes instead of raw HTML.
// Handles bold, italic, inline code, list items, and line breaks.
// ---------------------------------------------------------------------------

type TextSegment =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'italic'; value: string }
  | { type: 'code'; value: string };

function parseInline(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  // Match **bold**, *italic*, `code` in order of priority
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        value: text.slice(lastIndex, match.index),
      });
    }
    if (match[1] !== undefined) {
      segments.push({ type: 'bold', value: match[1] });
    } else if (match[2] !== undefined) {
      segments.push({ type: 'italic', value: match[2] });
    } else if (match[3] !== undefined) {
      segments.push({ type: 'code', value: match[3] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments;
}

function renderSegment(seg: TextSegment, key: number) {
  switch (seg.type) {
    case 'bold':
      return <strong key={key}>{seg.value}</strong>;
    case 'italic':
      return <em key={key}>{seg.value}</em>;
    case 'code':
      return (
        <code
          key={key}
          className="rounded bg-white/10 px-1 py-0.5 font-mono text-[0.9em]"
        >
          {seg.value}
        </code>
      );
    default:
      return <span key={key}>{seg.value}</span>;
  }
}

function MarkdownText({ text }: { text: string }) {
  const lines = text.split('\n');

  return (
    <span className="leading-relaxed">
      {lines.map((line, lineIdx) => {
        // List items
        const listMatch = /^- (.+)$/.exec(line);
        const content = listMatch ? `\u2022 ${listMatch[1]}` : line;
        const segments = parseInline(content);

        return (
          <span key={lineIdx}>
            {lineIdx > 0 && <br />}
            {listMatch ? (
              <span className="block">
                {segments.map((seg, i) => renderSegment(seg, i))}
              </span>
            ) : (
              segments.map((seg, i) => renderSegment(seg, i))
            )}
          </span>
        );
      })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Browser fingerprint (matches ChatSuite widget approach)
// ---------------------------------------------------------------------------

async function getFingerprint(): Promise<string> {
  const raw = [
    navigator.userAgent,
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    `${String(screen.width)}x${String(screen.height)}`,
    String(screen.colorDepth),
  ].join('|');

  const buffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(raw),
  );

  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatbotSection({
  cityName,
  hourlyRate,
  whatsappUrl,
  verticalSlug,
  citySlug,
  jurisdiction,
  verticalName,
  submitAction,
}: ChatbotSectionProps) {
  const experiment = useExperiment();
  const widgetToken =
    VERTICAL_WIDGET_TOKENS[verticalSlug] ?? DEFAULT_WIDGET_TOKEN;

  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [showLeadOverlay, setShowLeadOverlay] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!sessionStorage.getItem(`chatbot-lead:${verticalSlug}:${citySlug}`);
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initCalled = useRef(false);
  const engagementTracked = useRef(false);
  const userMessageCount = useRef(0);

  // Scroll chat container to bottom when messages change
  useEffect(() => {
    const container = messagesContainerRef.current;

    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isTyping]);

  // Focus input when user scrolls to this section via CTA (hash #chatbot)
  useEffect(() => {
    const section = document.getElementById('chatbot');
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && isReady && inputRef.current) {
          inputRef.current.focus({ preventScroll: true });
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [isReady]);

  const defaultWelcome = `Hi! I can help you learn about earning up to R${String(hourlyRate)}/hr driving in ${cityName}. What would you like to know?`;

  // Initialize session on mount
  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    const welcomeMsg: Message = {
      id: 'welcome',
      role: 'assistant',
      content: defaultWelcome,
    };

    // No token: enter demo mode with welcome message and working UI
    if (!widgetToken) {
      setIsReady(true);
      setShowFallback(true);
      setMessages([welcomeMsg]);
      return;
    }

    const timer = setTimeout(() => {
      setShowFallback(true);
    }, 5000);

    void (async () => {
      try {
        const fingerprint = await getFingerprint();

        const res = await fetch(`${API_URL}/api/widget/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Widget-Token': widgetToken,
          },
          body: JSON.stringify({ widgetToken, fingerprint }),
        });

        if (!res.ok) throw new Error(`Init failed: ${String(res.status)}`);

        const json = (await res.json()) as {
          success: boolean;
          data: {
            visitorId: string;
            conversationId: string;
            config?: { welcomeMessage?: string; agentName?: string };
          };
        };

        if (json.success) {
          clearTimeout(timer);
          setConversationId(json.data.conversationId);
          setVisitorId(json.data.visitorId);
          setIsReady(true);
          setMessages([
            {
              id: 'welcome',
              role: 'assistant',
              content: json.data.config?.welcomeMessage ?? defaultWelcome,
            },
          ]);
        }
      } catch {
        // Init failed: still show the UI with welcome message
        setIsReady(true);
        setShowFallback(true);
        setMessages([welcomeMsg]);
      }
    })();

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send message handler
  const sendMessage = useCallback(
    async (text: string) => {
      // ChatSuite creates the conversation lazily: `/api/widget/init` returns a
      // null conversationId and the real id arrives on the first message stream
      // (SSE `event: conversation`). So conversationId is null by design on the
      // opening message — only the token + visitor are required to send.
      if (!visitorId || !widgetToken || isTyping) return;

      // Track engagement on first message
      if (!engagementTracked.current) {
        engagementTracked.current = true;
        trackChatWidgetEngaged(verticalSlug, citySlug, experiment?.variant);
      }

      const userMsg: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };

      userMessageCount.current += 1;

      setMessages((prev) => [...prev, userMsg]);
      setShowQuickReplies(false);
      setIsTyping(true);
      setInputValue('');

      // Gate chatbot behind lead capture after N user messages
      if (
        userMessageCount.current >= LEAD_GATE_MESSAGE_COUNT &&
        !leadCaptured
      ) {
        trackChatbotLeadOverlayShown(
          verticalSlug,
          citySlug,
          userMessageCount.current,
          experiment?.variant,
        );
        setShowLeadOverlay(true);
        setIsTyping(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/widget/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Widget-Token': widgetToken,
          },
          body: JSON.stringify({
            widgetToken,
            conversationId,
            message: text,
            visitorId,
          }),
        });

        if (!res.ok || !res.body) {
          throw new Error(`Message failed: ${String(res.status)}`);
        }

        // Parse SSE stream
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let assistantContent = '';
        const assistantId = `assistant-${Date.now()}`;

        // Add empty assistant message to build up
        setMessages((prev) => [
          ...prev,
          { id: assistantId, role: 'assistant', content: '' },
        ]);
        setIsTyping(false);

        let buffer = '';
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') {
                streamDone = true;
                break;
              }

              try {
                const parsed = JSON.parse(data) as {
                  text?: string;
                  conversationId?: string;
                };

                // The `event: conversation` frame carries the real, lazily
                // created conversationId — capture it so follow-up messages
                // reference a stable conversation instead of re-creating one.
                if (parsed.conversationId) {
                  setConversationId(parsed.conversationId);
                }

                if (parsed.text) {
                  assistantContent += parsed.text;

                  if (assistantContent.length > MAX_RESPONSE_LENGTH) {
                    streamDone = true;
                    break;
                  }

                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, content: assistantContent }
                        : m,
                    ),
                  );
                }
              } catch {
                // Skip malformed SSE data
              }
            }
          }
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content:
              'Sorry, something went wrong. Please try again or apply via WhatsApp.',
          },
        ]);
      } finally {
        setIsTyping(false);
      }
    },
    [
      conversationId,
      visitorId,
      widgetToken,
      isTyping,
      leadCaptured,
      verticalSlug,
      citySlug,
      experiment?.variant,
    ],
  );

  const handleLeadCaptured = useCallback(
    (leadId?: string) => {
      setLeadCaptured(true);
      setShowLeadOverlay(false);
      sessionStorage.setItem(
        `chatbot-lead:${verticalSlug}:${citySlug}`,
        JSON.stringify({ leadId, capturedAt: new Date().toISOString() }),
      );
      trackChatbotLeadCaptured(
        verticalSlug,
        citySlug,
        leadId,
        experiment?.variant,
      );
    },
    [verticalSlug, citySlug, experiment?.variant],
  );

  const handleChipClick = useCallback(
    (chipText: string) => {
      trackChatbotChipClicked(
        verticalSlug,
        citySlug,
        chipText,
        experiment?.variant,
      );
      void sendMessage(chipText);
    },
    [verticalSlug, citySlug, experiment?.variant, sendMessage],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = inputValue.trim();

      if (text) void sendMessage(text);
    },
    [inputValue, sendMessage],
  );

  return (
    <section
      id="chatbot"
      className="relative px-4 py-16 sm:py-24"
      style={{ backgroundColor: 'var(--landing-secondary)' }}
    >
      <div className="mx-auto max-w-2xl">
        {/* Heading */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Chat with our recruitment team
          </h2>
          <p className="mt-3 text-lg text-white/60">
            Ask anything about driving in {cityName}. Our AI assistant responds
            instantly.
          </p>
        </div>

        {/* Chat card */}
        <div className="relative flex h-[520px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111]">
          {/* Message list */}
          <div
            ref={messagesContainerRef}
            className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[var(--landing-primary)] text-black'
                      : 'bg-[#1e1e1e] text-white'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <MarkdownText text={msg.content} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex gap-1.5 rounded-2xl bg-white/10 px-4 py-3">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-white/40 [animation-delay:0ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-white/40 [animation-delay:150ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-white/40 [animation-delay:300ms]" />
                </div>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {showQuickReplies && isReady && (
            <div className="flex flex-wrap gap-2 border-t border-white/10 px-4 py-3 sm:px-6">
              {QUICK_REPLIES.map((text) => (
                <button
                  key={text}
                  type="button"
                  onClick={() => handleChipClick(text)}
                  className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white transition-all hover:bg-white/10"
                >
                  {text}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 border-t border-white/10 px-4 py-3 sm:px-6"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={isReady ? 'Type a message...' : 'Connecting...'}
              aria-label="Chat message"
              disabled={!isReady || isTyping || showLeadOverlay}
              className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-base text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={
                !isReady || isTyping || showLeadOverlay || !inputValue.trim()
              }
              aria-label="Send message"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all disabled:opacity-30"
              style={{ backgroundColor: 'var(--landing-primary)' }}
            >
              <SendIcon className="h-4 w-4 text-black" />
            </button>
          </form>

          {/* Lead capture overlay */}
          {showLeadOverlay && (
            <ChatbotLeadOverlay
              verticalSlug={verticalSlug}
              citySlug={citySlug}
              jurisdiction={jurisdiction}
              verticalName={verticalName}
              submitAction={submitAction}
              onLeadCaptured={handleLeadCaptured}
            />
          )}
        </div>

        {/* WhatsApp fallback */}
        {showFallback && whatsappUrl && (
          <div className="mt-6 text-center">
            <p className="mb-3 text-sm text-white/40">
              Prefer WhatsApp? Chat with us directly.
            </p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-white/10"
            >
              <MessageCircleIcon className="h-4 w-4" />
              Apply via WhatsApp
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
